import {
  Activity,
  BellRing,
  Blocks,
  BadgeDollarSign,
  BriefcaseBusiness,
  Building2,
  CarFront,
  CarTaxiFront,
  ChevronDown,
  FileClock,
  FileSpreadsheet,
  FileSignature,
  Files,
  HeartPulse,
  House,
  Landmark,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  PlugZap,
  ReceiptText,
  Settings,
  Workflow,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { PlatformModule } from "@yatta/tenant-config";
import { useAuth } from "../auth/AuthProvider";
import { useTenantRuntime } from "../config/TenantRuntime";
import { env } from "../lib/env";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  module: PlatformModule;
  requiredModules?: PlatformModule[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: "Operação",
    items: [
      { to: "/", label: "Início", icon: LayoutDashboard, module: "dashboard" },
      { to: "/clientes", label: "Clientes", icon: Users, module: "customers" },
      { to: "/equipe", label: "Equipe comercial", icon: Users, module: "sales" },
      { to: "/contratos", label: "Contratos", icon: FileSignature, module: "contracts" },
      { to: "/telemedicina", label: "Telemedicina", icon: HeartPulse, module: "telemedicine" },
      { to: "/assistencia", label: "Assistência", icon: Activity, module: "funeral_assistance" },
    ],
  },
  {
    label: "Gestão",
    items: [
      { to: "/produtos", label: "Produtos", icon: Package, module: "catalog" },
      { to: "/vendas", label: "Vendas", icon: BriefcaseBusiness, module: "sales" },
      { to: "/financeiro", label: "Financeiro", icon: ReceiptText, module: "billing" },
      { to: "/comissoes", label: "Comissões", icon: BadgeDollarSign, module: "commissions" },
      { to: "/configuracoes", label: "Configurações", icon: Settings, module: "settings" },
      { to: "/workflows", label: "Workflows", icon: Workflow, module: "workflow_engine" },
      { to: "/notificacoes", label: "Notificações", icon: BellRing, module: "notifications" },
      { to: "/modelos-documentos", label: "Documentos", icon: Files, module: "document_builder" },
      {
        to: "/importacoes",
        label: "Importações",
        icon: FileSpreadsheet,
        module: "universal_importer",
      },
      { to: "/integracoes", label: "Integrações", icon: PlugZap, module: "integrations" },
    ],
  },
  {
    label: "Patrimônio",
    items: [
      { to: "/patrimonio", label: "Patrimônio", icon: Landmark, module: "assets" },
      {
        to: "/vendas-veiculos",
        label: "Venda de veículos",
        icon: CarFront,
        module: "vehicle_sales",
      },
      {
        to: "/locacoes-imoveis",
        label: "Locação de imóveis",
        icon: Building2,
        module: "property_leases",
      },
      {
        to: "/locacoes-veiculos",
        label: "Locação de veículos",
        icon: CarTaxiFront,
        module: "vehicle_rentals",
      },
      {
        to: "/despesas-ativos",
        label: "Despesas dos ativos",
        icon: Wrench,
        module: "asset_expenses",
      },
      {
        to: "/recebiveis",
        label: "Recebíveis",
        icon: FileClock,
        module: "receivable_schedules",
        requiredModules: ["assets"],
      },
      {
        to: "/fabrica-produto",
        label: "Fábrica do produto",
        icon: Blocks,
        module: "custom_fields",
      },
    ],
  },
];

const titles: Record<string, string> = {
  "/": "Visão geral",
  "/clientes": "Clientes",
  "/clientes/novo": "Novo cliente",
  "/vendas": "Vendas",
  "/equipe": "Gestão de vendedores",
  "/produtos": "Produtos e ofertas",
  "/contratos": "Contratos",
  "/financeiro": "Financeiro",
  "/comissoes": "Comissões",
  "/telemedicina": "Telemedicina",
  "/assistencia": "Assistência funerária",
  "/patrimonio": "Patrimônio",
  "/vendas-veiculos": "Venda de veículos",
  "/locacoes-imoveis": "Locação de imóveis",
  "/locacoes-veiculos": "Locação de veículos",
  "/despesas-ativos": "Despesas dos ativos",
  "/recebiveis": "Recebíveis",
  "/workflows": "Workflows",
  "/notificacoes": "Notificações",
  "/modelos-documentos": "Modelos de documentos",
  "/importacoes": "Importações",
  "/fabrica-produto": "Fábrica do produto",
  "/integracoes": "Integrações",
  "/configuracoes": "Configurações",
  "/yatta": "Clientes da plataforma",
  "/yatta/suporte": "Console de suporte",
  "/yatta/fabrica": "Fábrica de tenants",
  "/yatta/faturamento": "Faturamento SaaS",
};

export function AppShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const { tenant, hasModule } = useTenantRuntime();
  const { displayName, isPlatformMember, signOut } = useAuth();
  const assetVertical = hasModule("assets");
  const title =
    titles[location.pathname] ??
    (location.pathname.startsWith("/clientes/")
      ? assetVertical
        ? "Detalhes do cliente"
        : "Detalhes da família"
      : tenant.branding.shortName);
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  const visibleGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items
        .map((item) =>
          item.to === "/clientes"
            ? { ...item, label: assetVertical ? "Clientes e empresas" : "Clientes" }
            : item,
        )
        .filter(
          (item) =>
            hasModule(item.module) &&
            (item.requiredModules ?? []).every((module) => hasModule(module)),
        ),
    }))
    .filter((group) => group.items.length > 0);
  const isHome = location.pathname === "/";

  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? "sidebar-open" : ""}`}>
        <div className="brand">
          <div className="brand-mark">
            <House size={22} />
          </div>
          <div>
            <strong>{tenant.branding.shortName}</strong>
            <span>Uma solução Yatta Tecnologia</span>
          </div>
          <button
            className="sidebar-close"
            type="button"
            aria-label="Fechar menu"
            onClick={() => setOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Navegação principal">
          {visibleGroups.map((group) => (
            <section className="nav-group" key={group.label}>
              {group.label === "Operação" ? (
                <p className="nav-group-label">{group.label}</p>
              ) : (
                <button
                  className="nav-group-toggle"
                  type="button"
                  aria-expanded={Boolean(expandedGroups[group.label])}
                  onClick={() =>
                    setExpandedGroups((current) => ({
                      ...current,
                      [group.label]: !current[group.label],
                    }))
                  }
                >
                  {group.label}
                  <ChevronDown size={16} aria-hidden="true" />
                </button>
              )}
              {(group.label === "Operação" || expandedGroups[group.label]) &&
                group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      end={item.to === "/"}
                      key={item.to}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
            </section>
          ))}
          {isPlatformMember && (
            <>
              <NavLink
                to="/yatta"
                onClick={() => setOpen(false)}
                className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
              >
                <Building2 size={18} />
                <span>Plataforma Yatta</span>
              </NavLink>
              <NavLink
                to="/yatta/fabrica"
                onClick={() => setOpen(false)}
                className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
              >
                <Blocks size={18} />
                <span>Fábrica de tenants</span>
              </NavLink>
              <NavLink
                to="/yatta/suporte"
                onClick={() => setOpen(false)}
                className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
              >
                <Activity size={18} />
                <span>Suporte Yatta</span>
              </NavLink>
              <NavLink
                to="/yatta/faturamento"
                onClick={() => setOpen(false)}
                className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
              >
                <BadgeDollarSign size={18} />
                <span>Faturamento SaaS</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <span className="environment-pill">
            {env.useMocks ? "DEMONSTRAÇÃO" : tenant.product.releaseChannel.toUpperCase()}
          </span>
          <small>
            {env.useMocks
              ? "Dados fictícios para apresentação"
              : `${tenant.slug} · ${tenant.product.planCode}`}
          </small>
        </div>
      </aside>

      {open && (
        <button
          className="sidebar-backdrop"
          aria-label="Fechar menu"
          onClick={() => setOpen(false)}
        />
      )}

      <main className="main-area">
        <header className="topbar">
          <button
            className="menu-button"
            type="button"
            aria-label="Abrir menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <Menu size={21} />
          </button>
          {!isHome && (
            <div>
              <p className="eyebrow">CRUZEIRO LIFE · YATTA TECNOLOGIA</p>
              <h1>{title}</h1>
            </div>
          )}
          <div className="topbar-actions">
            {env.useMocks ? (
              <span
                className="demo-context"
                aria-label="Ambiente de demonstração segura com dados fictícios"
              >
                Demonstração segura
              </span>
            ) : (
              <>
                <button
                  className="user-avatar"
                  type="button"
                  title={`${displayName} — sair`}
                  onClick={() => void signOut()}
                >
                  {initials || "YT"}
                </button>
                <LogOut className="logout-icon" size={16} aria-hidden="true" />
              </>
            )}
          </div>
        </header>
        <div className="content">{children}</div>
      </main>
    </div>
  );
}
